const REPLY_PRODUCT_PRICE_LIMIT = 1000000000;

function normalizePriceParts(value, allowZero) {
  if (typeof value !== "string" || !/^[\d,\s원~]+$/.test(value)) return null;
  const parts = value.split("~");
  if (parts.length > 2 || parts.some((part) => !part)) return null;

  const numbers = parts.map((part) => {
    const compact = part.replace(/\s/g, "");
    if (!/^(?:\d+|\d{1,3}(?:,\d{3})+)원?$/.test(compact)) return null;
    return Number(compact.replace(/원$/, "").replace(/,/g, "").replace(/^0+/, "") || "0");
  });
  if (
    numbers.some(
      (number) =>
        number === null ||
        !Number.isSafeInteger(number) ||
        number > REPLY_PRODUCT_PRICE_LIMIT ||
        (!allowZero && number <= 0)
    ) ||
    (numbers.length === 2 && numbers[0] > numbers[1])
  ) {
    return null;
  }
  return numbers;
}

function normalizeReplyProductPrice(value) {
  const numbers = normalizePriceParts(value, false);
  return numbers ? numbers.join("~") : "";
}

function normalizeReplyProductAdditionalPrice(value) {
  const numbers = normalizePriceParts(value, true);
  return numbers ? numbers.join("~") : "";
}

function formatReplyProductPrice(value) {
  const normalized = normalizeReplyProductAdditionalPrice(value);
  if (!normalized) return "";
  const formatted = normalized
    .split("~")
    .map((part) => `${Number(part).toLocaleString("en-US")}원`);
  return formatted.length === 1
    ? formatted[0]
    : `최소 ${formatted[0]} ~ ${formatted[1]}까지`;
}

function hasExplicitAdditionalPrice(material) {
  return Boolean(
    material &&
      Object.prototype.hasOwnProperty.call(material, "additionalPrice") &&
      material.additionalPrice !== undefined &&
      material.additionalPrice !== null &&
      String(material.additionalPrice).trim() !== ""
  );
}

function sumReplyProductPrices(basePrice, additionalPrice) {
  const baseParts = normalizePriceParts(basePrice, false);
  const additionalParts = normalizePriceParts(additionalPrice, true);
  if (!baseParts || !additionalParts) return "";

  const sumParts = [0, 1].map((index) => {
    const base = baseParts[Math.min(index, baseParts.length - 1)];
    const additional = additionalParts[Math.min(index, additionalParts.length - 1)];
    const sum = base + additional;
    return Number.isSafeInteger(sum) && sum <= REPLY_PRODUCT_PRICE_LIMIT
      ? sum
      : null;
  });
  if (sumParts.some((part) => part === null)) return "";
  return sumParts[0] === sumParts[1] ? String(sumParts[0]) : sumParts.join("~");
}

function calculateReplyProductMaterialPriceTexts(product) {
  if (!product || !product.material) return null;
  const base = product.material.base;
  if (base !== "14K" && base !== "18K") return null;

  const price = normalizeReplyProductPrice(product.price);
  if (!price) return null;

  const hasAdditionalPrice = hasExplicitAdditionalPrice(product.material);
  const additionalPrice = hasAdditionalPrice
    ? normalizeReplyProductAdditionalPrice(product.material.additionalPrice)
    : "";
  if (hasAdditionalPrice && !additionalPrice) return null;

  const price18K = hasAdditionalPrice
    ? sumReplyProductPrices(price, additionalPrice)
    : "";
  if (hasAdditionalPrice && !price18K) return null;

  return {
    price,
    material: { base, additionalPrice },
    hasAdditionalPrice,
    minimumPriceText: formatReplyProductPrice(price.split("~")[0]),
    price14KText: formatReplyProductPrice(price),
    price18KText: price18K ? formatReplyProductPrice(price18K) : "",
  };
}

function normalizeReplyProductMaterialPrices(product) {
  const calculated = calculateReplyProductMaterialPriceTexts(product);
  if (
    !calculated ||
    (calculated.material.base === "18K" && !calculated.hasAdditionalPrice)
  ) {
    return null;
  }
  return calculated;
}

function getReplyProductsMinimumPrice(products) {
  if (!Array.isArray(products) || !products.length) return "";
  const prices = products
    .map((product) => normalizeReplyProductPrice(product && product.price))
    .filter(Boolean)
    .map((price) => Number(price.split("~")[0]));
  return prices.length ? String(Math.min(...prices)) : "";
}

module.exports = {
  calculateReplyProductMaterialPriceTexts,
  formatReplyProductPrice,
  getReplyProductsMinimumPrice,
  normalizeReplyProductAdditionalPrice,
  normalizeReplyProductMaterialPrices,
  normalizeReplyProductPrice,
  sumReplyProductPrices,
};
